import React, { ChangeEventHandler, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import pluralize from 'pluralize';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { Suppliers } from '@luxuryescapes/contract-svc-bedbank/constants/suppliers';

import { SUPPLIER_NAMES } from '~/consts/bedbank';

import bedbankService, { type MLMatchingCandidatesResult, type SourceRoomMLResult } from '~/services/BedbankService';

import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';

import ControlRoomElement from './RoomMapping/ControlRoomElement';
import SourceRoomElement from './RoomMapping/SourceRoomElement';
import { getGroupKey, groupSourceRoomsBySupplierName } from './RoomMapping/grouping';
import type {
  AutomappingFlagToggleTarget,
  MappedRoom,
  MappingChange,
  PropertyRoom,
  SourceRoom,
  SourceRoomListItem,
} from './RoomMapping/types';
import { isSourceRoomGroup } from './RoomMapping/types';
import { matchesSourceRoomSearch } from './RoomMapping/util';

// ============================================================================
// Constants
// ============================================================================

/** Threshold for source rooms above which ML data is not auto-fetched */
const ML_AUTO_FETCH_SOURCE_ROOM_THRESHOLD = 500;

/** Threshold for property rooms above which ML data is not auto-fetched */
const ML_AUTO_FETCH_PROPERTY_ROOM_THRESHOLD = 50;

/** Vertical height for the virtualised source/master lists. */
const VIRTUOSO_LIST_HEIGHT = '75vh';

/** Debounce delay for quick map operations (ms) */
const QUICK_MAP_DEBOUNCE_MS = 500;

/** Delay before scrolling to mapped room (ms) */
const SCROLL_DELAY_MS = 100;

/** No-op function for fallback */
const NOOP = () => {
  /* intentionally empty */
};

// ============================================================================
// Types
// ============================================================================

interface Room extends App.Bedbank.RoomEdit {
  isSelected?: boolean;
  useOverrideNameForMapping?: boolean;
}

type SourceRoomResponse = App.Bedbank.SupplierSourceRoom & {
  extraInclusions?: Array<{ code: string; label: string }>;
};

type SourceRoomsBySupplier = {
  [Supplier in App.Bedbank.Suppliers]: Array<SourceRoom>;
};

interface Props {
  property: App.Bedbank.PropertyEdit;
  onForceDecoupleRoomsClick: (ids: Array<string>) => void;
  handleChange: ({
    update,
    remove,
    automappingFlagUpdate,
  }: {
    update: App.Bedbank.SupplierRoomMappingPayload;
    remove: App.Bedbank.SupplierRoomMappingPayload;
    automappingFlagUpdate: App.Bedbank.SupplierRoomAutomappingFlagPayload;
  }) => void;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Formats source rooms from API response, stripping HTML tags from Expedia descriptions
 */
const formatSource = (rooms: Array<SourceRoomResponse>, supplier: App.Bedbank.Suppliers): Array<SourceRoom> =>
  rooms.map((room) => ({
    ...room,
    supplier,
    // Remove HTML tags from description for Expedia rooms
    description: supplier === Suppliers.expedia ? room.description.replace(/<[^>]*>?/gm, '') : room.description,
    extraInclusions: room.extraInclusions ?? [],
    hasInfo: Boolean(
      room.amenities.length ||
        room.bedGroups.length ||
        room.views.length ||
        (room.extraInclusions?.length ?? 0) ||
        Object.values(room.occupancy ?? {}).length,
    ),
  }));

/**
 * Sorts source rooms by info availability first, then by availability score (descending)
 */
const sortSourceRoom = (a: SourceRoom, b: SourceRoom): number => {
  // Rooms with info come first
  if (a.hasInfo && !b.hasInfo) {
    return -1;
  }
  if (!a.hasInfo && b.hasInfo) {
    return 1;
  }

  // Both null/undefined (intentionally using loose equality for null check)
  if (a.availabilityScore == null && b.availabilityScore == null) {
    return 0;
  }
  if (a.availabilityScore == null) {
    return 1;
  }
  if (b.availabilityScore == null) {
    return -1;
  }

  return b.availabilityScore - a.availabilityScore;
};

/**
 * Extracts supplier and external ID from a composite key
 * @param key Format: "supplier-externalId"
 * @returns [supplier, externalId]
 */
const extractKey = (key: string): [App.Bedbank.Suppliers, string] => {
  const [supplier, ...rest] = key.split('-');
  return [supplier as App.Bedbank.Suppliers, rest.join('-')];
};

// ============================================================================
// Main Component
// ============================================================================

function BedbankRoomMapping({ property, handleChange, onForceDecoupleRoomsClick }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  // ========================================
  // State: Loading & Data
  // ========================================

  const [isLoading, setIsLoading] = useState(false);
  const [sourceRooms, setSourceRooms] = useState<SourceRoomsBySupplier>(
    SUPPLIER_NAMES.reduce((acc, item) => {
      acc[item] = [];
      return acc;
    }, {} as SourceRoomsBySupplier),
  );
  const [propertyRooms, setPropertyRooms] = useState<Array<PropertyRoom>>([]);
  const [alreadyMappedRooms, setAlreadyMappedRooms] = useState<Record<string, boolean>>({});

  // Dynamic list of suppliers that have mapped rooms
  const propertyRoomSuppliers = useMemo(() => {
    const supplierSet = new Set<string>();
    let hasMasterSupplierMappings = false;

    propertyRooms.forEach((room) => {
      room.mappedRooms.forEach((mappedRoom) => {
        supplierSet.add(mappedRoom.supplier);
        if (mappedRoom.supplier === property.masterSupplier) {
          hasMasterSupplierMappings = true;
        }
      });
    });

    const orderedSuppliers = Array.from(supplierSet.values()).filter(Boolean);

    if (hasMasterSupplierMappings && !orderedSuppliers.includes(property.masterSupplier)) {
      orderedSuppliers.unshift(property.masterSupplier);
    }

    return orderedSuppliers;
  }, [propertyRooms, property]);

  // ========================================
  // State: Mapping Operations
  // ========================================

  const [updatePayload, setUpdatePayload] = useState<App.Bedbank.SupplierRoomMappingPayload>([]);
  const [removePayload, setRemovePayload] = useState<App.Bedbank.SupplierRoomMappingPayload>([]);
  const [automappingFlagPayload, setAutomappingFlagPayload] = useState<App.Bedbank.SupplierRoomAutomappingFlagPayload>(
    [],
  );
  const [appliedButNotSavedRooms, setAppliedButNotSavedRooms] = useState<Array<SourceRoom>>([]);

  // ========================================
  // State: Selection
  // ========================================

  const [selectedSourceRooms, setSelectedSourceRooms] = useState<Array<string>>([]);
  const [selectedPropertyRoom, setSelectedPropertyRoom] = useState<string | null>(null);

  // ========================================
  // State: Search & Filters
  // ========================================

  // Get roomId and mappedRoomId from URL query params for initial filter
  const getUrlParams = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      roomId: urlParams.get('roomId') || '',
      mappedRoomId: urlParams.get('mappedRoomId') || '',
    };
  }, []);

  // Property room search
  const [propertySearchTerm, setPropertySearchTerm] = useState(() => getUrlParams().mappedRoomId);
  const resetPropertySearch = useCallback(() => {
    setPropertySearchTerm('');
  }, []);
  const handlePropertySearchChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setPropertySearchTerm(event.target.value);
  }, []);

  // Source room search
  const [sourceSearchTerm, setSourceSearchTerm] = useState(() => getUrlParams().roomId);
  const resetSourceSearch = useCallback(() => {
    setSourceSearchTerm('');
  }, []);
  const handleSourceSearchChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSourceSearchTerm(event.target.value);
  }, []);

  // Deferred values for expensive filtering operations
  const deferredPropertySearchTerm = useDeferredValue(propertySearchTerm);
  const deferredSourceSearchTerm = useDeferredValue(sourceSearchTerm);

  // Track if filtering is in progress (for UI feedback)
  const isFilteringPropertyRooms = deferredPropertySearchTerm !== propertySearchTerm;
  const isFilteringSourceRooms = deferredSourceSearchTerm !== sourceSearchTerm;

  // Supplier filters
  const [sourceSupplierFilter, setSourceSupplierFilter] = useState<string>('');
  const handleSourceSupplierFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setSourceSupplierFilter(event.target.value);
  }, []);

  const [propertySupplierFilter, setPropertySupplierFilter] = useState<string>('');
  const handlePropertySupplierFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setPropertySupplierFilter(event.target.value);
  }, []);

  // ========================================
  // State: UI Controls
  // ========================================

  const [hideRoomsWithNoAvailabilityScore, setHideRoomsWithNoAvailabilityScore] = useState<boolean>(false);
  const [showOnlyRoomsWithInclusions, setShowOnlyRoomsWithInclusions] = useState<boolean>(false);
  const [enableMLMatching, setEnableMLMatching] = useState<boolean>(false);
  const [ungroupedKeys, setUngroupedKeys] = useState<Set<string>>(new Set());
  const [sortSourceRoomsBy, setSortSourceRoomsBy] = useState<'ml-score' | 'availability'>('ml-score');

  // ========================================
  // State: ML Matching
  // ========================================

  // Track if source rooms have been loaded (to enable ML loading)
  const [sourceRoomsLoaded, setSourceRoomsLoaded] = useState(false);

  // Track if user has manually triggered ML fetch (for large datasets)
  const [manualMLFetchRequested, setManualMLFetchRequested] = useState(false);

  // ML matching candidates from API
  const [mlMatchingCandidates, setMLMatchingCandidates] = useState<MLMatchingCandidatesResult>({});
  const [isLoadingMLCandidates, setIsLoadingMLCandidates] = useState(false);

  // ML match scores for sorting (initialized from localStorage)
  const [mlMatchScores, setMLMatchScores] = useState<Record<string, number>>(() => {
    try {
      const cacheKey = `mlMatchScores-${property.id}`;
      const cached = localStorage.getItem(cacheKey);
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });

  // ========================================
  // State: Quick Map
  // ========================================

  const [isQuickMapping, setIsQuickMapping] = useState(false);

  // ========================================
  // Refs
  // ========================================

  const quickMapInProgressRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const propertyRoomRefs = useRef<Record<string, HTMLElement | null>>({});
  const propertyVirtuosoRef = useRef<VirtuosoHandle>(null);

  // ========================================
  // Event Handlers: UI Toggles
  // ========================================

  const toggleHideRoomsWithNoAvailabilityScore = () => {
    setHideRoomsWithNoAvailabilityScore((prev) => !prev);
  };

  const toggleShowOnlyRoomsWithInclusions = () => {
    setShowOnlyRoomsWithInclusions((prev) => !prev);
  };

  const toggleMLMatching = () => {
    setEnableMLMatching((prev) => !prev);
  };

  const handleSortChange = (event: SelectChangeEvent<string>) => {
    setSortSourceRoomsBy(event.target.value as 'ml-score' | 'availability');
  };

  // ========================================
  // Effects: Cleanup
  // ========================================

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // ========================================
  // Event Handlers: Room Grouping
  // ========================================

  const handleUngroupRoom = useCallback((supplier: App.Bedbank.Suppliers, supplierRoomName: string) => {
    const key = getGroupKey(supplier, supplierRoomName);
    setUngroupedKeys((prev) => {
      const newSet = new Set(prev);
      newSet.add(key);
      return newSet;
    });
  }, []);

  // ========================================
  // Computed: Filtered Rooms
  // ========================================

  // Performance optimization: Normalize search term once
  const normalizedPropertySearchTerm = useMemo(
    () => (deferredPropertySearchTerm ?? '').trim().toLowerCase(),
    [deferredPropertySearchTerm],
  );

  const normalizedSourceSearchTerm = useMemo(
    () => (deferredSourceSearchTerm ?? '').trim().toLowerCase(),
    [deferredSourceSearchTerm],
  );

  const { filteredPropertyRooms, verifiedMappingMatchesByRoomId } = useMemo(() => {
    const notExcludedRooms = propertyRooms.filter((room) => room.status !== 'excluded');
    const isSearching = normalizedPropertySearchTerm.length > 0;
    const matchesByRoomId: Record<string, Array<string>> = {};

    const filteredRooms = isSearching
      ? notExcludedRooms.filter((room) => {
          // Search both supplier name (primary display) and override name
          const supplierNameMatch = room.name.toLowerCase().includes(normalizedPropertySearchTerm);
          const overrideNameMatch = room.nameOverride
            ? room.nameOverride.toLowerCase().includes(normalizedPropertySearchTerm)
            : false;
          const roomNameMatch = supplierNameMatch || overrideNameMatch;
          const propertyExternalMatch = room.externalId
            ? room.externalId.toLowerCase().includes(normalizedPropertySearchTerm)
            : false;

          const activeMappedRooms = room.mappedRooms.filter(
            (mappedRoom) => alreadyMappedRooms[`${mappedRoom.supplier}-${mappedRoom.externalId}`],
          );

          const verifiedMappedRooms = activeMappedRooms.filter((mappedRoom) => mappedRoom.isVerified);
          const verifiedMatches: Array<string> = [];

          verifiedMappedRooms.forEach((mappedRoom) => {
            const codeMatch = mappedRoom.externalId.toLowerCase().includes(normalizedPropertySearchTerm);
            const sourceName = mappedRoom.sourceContent?.name?.toLowerCase() ?? '';
            const nameMatch = sourceName.includes(normalizedPropertySearchTerm);

            if (codeMatch || nameMatch) {
              verifiedMatches.push(mappedRoom.id);
            }
          });

          if (verifiedMatches.length > 0) {
            matchesByRoomId[room.id] = Array.from(new Set(verifiedMatches));
          }

          const mappedRoomCodeMatch = activeMappedRooms.some((mappedRoom) =>
            mappedRoom.externalId.toLowerCase().includes(normalizedPropertySearchTerm),
          );

          return roomNameMatch || mappedRoomCodeMatch || propertyExternalMatch || verifiedMatches.length > 0;
        })
      : notExcludedRooms;

    const processedRooms = hideRoomsWithNoAvailabilityScore
      ? filteredRooms.map((propertyRoom) => {
          return {
            ...propertyRoom,
            mappedRooms: propertyRoom.mappedRooms.filter(
              (mappedRoom) =>
                mappedRoom.sourceContent.availabilityScore !== null || mappedRoom.supplier === propertyRoom.supplier,
            ),
          };
        })
      : filteredRooms;

    const sortedRooms = processedRooms.sort((a, b) => {
      const hasUnverifiedA = a.mappedRooms.some(
        (mappedRoom) => !mappedRoom.isVerified && mappedRoom.externalId !== a.externalId,
      );
      const hasUnverifiedB = b.mappedRooms.some(
        (mappedRoom) => !mappedRoom.isVerified && mappedRoom.externalId !== b.externalId,
      );

      if (hasUnverifiedA !== hasUnverifiedB) {
        return hasUnverifiedB ? 1 : -1;
      }

      return 0;
    });

    return {
      filteredPropertyRooms: sortedRooms,
      verifiedMappingMatchesByRoomId: matchesByRoomId,
    };
  }, [propertyRooms, normalizedPropertySearchTerm, hideRoomsWithNoAvailabilityScore, alreadyMappedRooms]);

  const filteredSourceRooms = useMemo(() => {
    const initialRooms = SUPPLIER_NAMES.flatMap((supplier) => sourceRooms[supplier] ?? []);

    // Performance: Short-circuit if no filters applied
    if (
      !normalizedSourceSearchTerm &&
      !sourceSupplierFilter &&
      !hideRoomsWithNoAvailabilityScore &&
      !showOnlyRoomsWithInclusions
    ) {
      return initialRooms.filter(
        (room) =>
          !appliedButNotSavedRooms.some((r) => r.externalId === room.externalId && r.supplier === room.supplier),
      );
    }

    // Apply all filters in a single pass
    return initialRooms.filter((room) => {
      // Search filter
      if (!matchesSourceRoomSearch(room, normalizedSourceSearchTerm)) {
        return false;
      }

      // Supplier filter
      if (sourceSupplierFilter && room.supplier !== sourceSupplierFilter) {
        return false;
      }

      // Availability score filter
      if (hideRoomsWithNoAvailabilityScore && room.availabilityScore === null) {
        return false;
      }

      // Inclusion filter
      if (showOnlyRoomsWithInclusions && room.extraInclusions.length === 0) {
        return false;
      }

      // Applied but not saved filter
      if (appliedButNotSavedRooms.some((r) => r.externalId === room.externalId && r.supplier === room.supplier)) {
        return false;
      }

      // Applied but not saved filter
      if (property.rooms.some((r) => r.externalId === room.externalId && r.supplier === room.supplier)) {
        return false;
      }

      return true;
    });
  }, [
    sourceRooms,
    normalizedSourceSearchTerm,
    sourceSupplierFilter,
    hideRoomsWithNoAvailabilityScore,
    showOnlyRoomsWithInclusions,
    appliedButNotSavedRooms,
    property,
  ]);

  const filteredUnmappedSourceRooms = useMemo(() => {
    // Filter out rooms that have already been mapped
    return filteredSourceRooms.filter((room) => !alreadyMappedRooms[`${room.supplier}-${room.externalId}`]);
  }, [filteredSourceRooms, alreadyMappedRooms]);

  // Helper to get the best ML match score for a source room
  const getBestMLScore = useCallback(
    (sourceRoom: SourceRoom): number => {
      const key = `${sourceRoom.supplier}-${sourceRoom.externalId}`;

      // If we have a cached ML score, use it
      if (mlMatchScores[key] !== undefined) {
        return mlMatchScores[key];
      }

      // Otherwise fall back to availability score as proxy
      return sourceRoom.availabilityScore ?? 0;
    },
    [mlMatchScores],
  );

  // No-op callback kept for component compatibility
  const updateMLMatchScore = useCallback((_sourceRoom: SourceRoom, _bestScore: number) => {
    // Scores are managed by the useEffect above
  }, []);

  const groupedUnmappedSourceRooms = useMemo(() => {
    // Note: This automatically respects supplier filter because filteredUnmappedSourceRooms
    // depends on filteredSourceRooms which includes supplierFilter in its filtering logic
    const grouped = groupSourceRoomsBySupplierName(filteredUnmappedSourceRooms, ungroupedKeys);

    // Sort based on user preference (ML match score or availability score)
    return grouped.sort((a, b) => {
      const roomA = isSourceRoomGroup(a) ? a.rooms[0] : a;
      const roomB = isSourceRoomGroup(b) ? b.rooms[0] : b;

      if (sortSourceRoomsBy === 'availability') {
        // Sort by availability score only
        const scoreA = roomA.availabilityScore ?? 0;
        const scoreB = roomB.availabilityScore ?? 0;
        return scoreB - scoreA; // Descending (highest first)
      } else {
        // Sort by ML match score (using availability as proxy for now)
        const scoreA = getBestMLScore(roomA);
        const scoreB = getBestMLScore(roomB);
        return scoreB - scoreA; // Descending (highest first)
      }
    });
  }, [filteredUnmappedSourceRooms, ungroupedKeys, getBestMLScore, sortSourceRoomsBy]);

  const totalSourceRoomCount = useMemo(() => {
    return SUPPLIER_NAMES.reduce((count, supplier) => count + (sourceRooms[supplier]?.length ?? 0), 0);
  }, [sourceRooms]);

  const extraInclusionsByKey = useMemo(() => {
    const map = new Map<string, Array<{ code: string; label: string }>>();
    for (const supplier of SUPPLIER_NAMES) {
      for (const room of sourceRooms[supplier] ?? []) {
        if (room.externalId && room.extraInclusions?.length) {
          map.set(`${supplier}:${room.externalId}`, room.extraInclusions);
        }
      }
    }
    return map;
  }, [sourceRooms]);

  const totalPropertyRoomCount = propertyRooms.length;

  const exceedsMLAutoFetchThreshold = useMemo(() => {
    return (
      totalSourceRoomCount > ML_AUTO_FETCH_SOURCE_ROOM_THRESHOLD &&
      totalPropertyRoomCount > ML_AUTO_FETCH_PROPERTY_ROOM_THRESHOLD
    );
  }, [totalSourceRoomCount, totalPropertyRoomCount]);

  const shouldFetchMLCandidates = sourceRoomsLoaded && (!exceedsMLAutoFetchThreshold || manualMLFetchRequested);

  const handleManualMLFetch = useCallback(() => {
    setManualMLFetchRequested(true);
  }, []);

  useEffect(() => {
    if (!shouldFetchMLCandidates) {
      return;
    }

    let isCancelled = false;

    async function fetchMLCandidates() {
      setIsLoadingMLCandidates(true);

      try {
        const response = await bedbankService.getMLMatchingCandidates(property.id);

        if (isCancelled) {
          return;
        }

        if (response?.result) {
          setMLMatchingCandidates(response.result);

          // Extract ML match scores from response for sorting
          const updatedScores: Record<string, number> = {};
          Object.values(response.result).forEach((sourceRoomResult: SourceRoomMLResult) => {
            if (sourceRoomResult.candidates && sourceRoomResult.candidates.length > 0) {
              sourceRoomResult.sourceRooms.forEach((compositeKey) => {
                const [supplier, , sourceRoomId] = compositeKey.split(':');
                const key = `${supplier}-${sourceRoomId}`;
                updatedScores[key] = sourceRoomResult.candidates[0].score;
              });
            }
          });

          // Update ML scores for sorting and persist to localStorage
          if (Object.keys(updatedScores).length > 0) {
            setMLMatchScores((prev) => {
              const merged = { ...prev, ...updatedScores };
              try {
                const cacheKey = `mlMatchScores-${property.id}`;
                localStorage.setItem(cacheKey, JSON.stringify(merged));
              } catch {
                // Silently fail localStorage errors
              }
              return merged;
            });
          }
        }
      } catch (error) {
        // ML candidates are non-critical, log and continue
        console.error('Failed to fetch ML matching candidates:', error);
      } finally {
        if (!isCancelled) {
          setIsLoadingMLCandidates(false);
        }
      }
    }

    fetchMLCandidates();

    return () => {
      isCancelled = true;
    };
  }, [shouldFetchMLCandidates, property.id]);

  const selectedFromSourceRooms = useMemo(() => {
    const selectedCount = selectedSourceRooms.length;
    const totalCount = filteredUnmappedSourceRooms.length;
    const unmappedRoomsWithNoAvailabilityScoreCount = filteredUnmappedSourceRooms.filter(
      (room) => room.availabilityScore === null,
    ).length;
    const unmappedRoomsWithAvailabilityScoreCount = totalCount - unmappedRoomsWithNoAvailabilityScoreCount;

    return selectedCount > 0
      ? `${selectedCount} selected of ${totalCount} unmapped ${pluralize('rooms', totalCount)}`
      : `${unmappedRoomsWithAvailabilityScoreCount} ${pluralize(
          'rooms',
          unmappedRoomsWithAvailabilityScoreCount,
        )} with availability score, ${unmappedRoomsWithNoAvailabilityScoreCount} ${pluralize(
          'rooms',
          unmappedRoomsWithNoAvailabilityScoreCount,
        )} without availability score`;
  }, [filteredUnmappedSourceRooms, selectedSourceRooms]);

  const selectedFromPropertyRooms = useMemo(() => {
    const selectedRoomId = selectedPropertyRoom ? extractKey(selectedPropertyRoom)[1] : null;
    const selectedRoom = selectedRoomId
      ? filteredPropertyRooms.find((room) => room.externalId === selectedRoomId)
      : null;
    const totalCount = filteredPropertyRooms.length;

    // Exclude rooms that has the same externalId since they would always be mapped to their property room counterpart
    const totalWithMappedRooms = filteredPropertyRooms.filter(
      (room) => room.mappedRooms.filter((mappedRoom) => mappedRoom.externalId !== room.externalId).length > 0,
    ).length;

    const totalWithoutMappedRooms = totalCount - totalWithMappedRooms;

    return selectedRoom
      ? `${selectedRoom.nameOverride ?? selectedRoom.name} selected`
      : `${totalWithMappedRooms} ${pluralize(
          'rooms',
          totalWithMappedRooms,
        )} with mapping, ${totalWithoutMappedRooms} ${pluralize('rooms', totalWithoutMappedRooms)} without mapping`;
  }, [selectedPropertyRoom, filteredPropertyRooms]);

  const updates = useMemo(() => {
    const updates: {
      [roomId: string]: Array<MappingChange>;
    } = {};

    for (const update of updatePayload) {
      if (!updates[update.roomId]) {
        updates[update.roomId] = [];
      }
      updates[update.roomId].push({
        supplier: update.supplier,
        externalId: update.supplierRoomId,
        isVerified: update.isVerified,
      });
    }
    return updates;
  }, [updatePayload]);

  const supplierExternalId = useCallback(
    (supplier: App.Bedbank.Suppliers): string => {
      if (SUPPLIER_NAMES.includes(supplier)) {
        return property[`${supplier}Id`];
      } else {
        throw new Error(`Unhandled supplier: ${supplier}`);
      }
    },
    [property],
  );

  const formatSourceRoom = useCallback(
    (
      mappedRoom: SourceRoom | MappedRoom,
      supplier: App.Bedbank.Suppliers,
      roomId: string,
      includeVerified = false,
    ): App.Bedbank.SupplierRoomMappingPayload[number] => {
      let mappingMethod: App.Bedbank.SupplierRoomMappingPayload[number]['mappingMethod'] = ['manual'];

      if ('mappingMethod' in mappedRoom && mappedRoom.mappingMethod?.length) {
        // Filter out 'ml' as it's not in the SupplierRoomMappingPayload type
        const filteredMethods = mappedRoom.mappingMethod.filter((method) => method !== 'ml');
        // Type narrowing: after filtering 'ml', the result matches the expected type
        if (filteredMethods.length > 0) {
          mappingMethod = filteredMethods;
        }
      }

      return {
        supplier: supplier,
        roomId,
        supplierRoomId: mappedRoom.externalId,
        supplierPropertyId: supplierExternalId(supplier),
        mappingMethod,
        isVerified: includeVerified ? ('isVerified' in mappedRoom ? mappedRoom.isVerified ?? true : true) : false,
      };
    },
    [supplierExternalId],
  );

  const getSourceRooms = useCallback(async (): Promise<SourceRoomsBySupplier> => {
    try {
      const response = await bedbankService.getSourceRooms(property.id);

      return SUPPLIER_NAMES.reduce((acc, item) => {
        acc[item] = formatSource(response.result[item], item);
        return acc;
      }, {} as SourceRoomsBySupplier);
    } catch (error) {
      console.error('Error fetching source rooms', error);
      return SUPPLIER_NAMES.reduce((acc, item) => {
        acc[item] = [];
        return acc;
      }, {} as SourceRoomsBySupplier);
    }
  }, [property.id]);

  const handleSourceRoomSelection = (room: SourceRoomListItem, supplier: App.Bedbank.Suppliers) => {
    setSelectedSourceRooms((currentSelected) => {
      const updatedSelection = [...currentSelected];

      if (isSourceRoomGroup(room)) {
        const keys = room.rooms.map((childRoom) => `${childRoom.supplier}-${childRoom.externalId ?? childRoom.id}`);
        const allSelected = keys.every((key) => updatedSelection.includes(key));

        if (allSelected) {
          return updatedSelection.filter((key) => !keys.includes(key));
        }

        keys.forEach((key) => {
          if (!updatedSelection.includes(key)) {
            updatedSelection.push(key);
          }
        });

        return updatedSelection;
      }

      const key = `${supplier}-${room.externalId ?? room.id}`;
      if (updatedSelection.includes(key)) {
        return updatedSelection.filter((selectedKey) => selectedKey !== key);
      }

      updatedSelection.push(key);
      return updatedSelection;
    });
  };

  const handlePropertyRoomSelection = (room: PropertyRoom, supplier: App.Bedbank.Suppliers) => {
    setSelectedPropertyRoom(`${supplier}-${room.externalId}`);
  };

  const applyAutomappingFlagChanges = (
    payload: App.Bedbank.SupplierRoomAutomappingFlagPayload,
    changes: Array<AutomappingFlagToggleTarget>,
  ) => {
    const nextPayload = [...payload];

    changes.forEach(({ supplierRoomId, externalPropertyId, supplier, isAutoMappingEnabled }) => {
      const index = nextPayload.findIndex(
        (flag) => flag.supplierRoomId === supplierRoomId && flag.supplier === supplier,
      );

      if (index !== -1) {
        const existingPayload = nextPayload[index];

        if (existingPayload.isAutoMappingEnabled !== isAutoMappingEnabled) {
          nextPayload.splice(index, 1);
        }
      } else {
        nextPayload.push({
          supplierRoomId,
          externalPropertyId,
          supplier,
          isAutoMappingEnabled,
        });
      }
    });

    return nextPayload;
  };

  const handleSetAutomappingFlags = (targets: AutomappingFlagToggleTarget | Array<AutomappingFlagToggleTarget>) => {
    const changes = Array.isArray(targets) ? targets : [targets];

    let newAutomappingFlagPayload: App.Bedbank.SupplierRoomAutomappingFlagPayload | undefined;

    setAutomappingFlagPayload((currentPayload) => {
      const updatedPayload = applyAutomappingFlagChanges(currentPayload, changes);
      newAutomappingFlagPayload = updatedPayload;
      return updatedPayload;
    });

    handleChange({
      update: updatePayload,
      remove: removePayload,
      automappingFlagUpdate: newAutomappingFlagPayload ?? automappingFlagPayload,
    });
  };

  const handleUpdateMapping = () => {
    // get all selected source rooms
    const nonMasterSupplierMappedRooms: Array<SourceRoom> = selectedSourceRooms
      .map((key) => {
        const [supplier, externalId] = extractKey(key);
        return sourceRooms[supplier].find((sr: SourceRoom) => sr.externalId === externalId);
      })
      .filter(Boolean); // remove any falsy values

    if (nonMasterSupplierMappedRooms.length === 0) {
      enqueueSnackbar('Please select at least one source room', { variant: 'error' });
      return;
    }
    const room = selectedPropertyRoom
      ? propertyRooms.find((r) => r.externalId === extractKey(selectedPropertyRoom)[1])
      : null;

    if (!room) {
      enqueueSnackbar('Please select a room from property rooms', { variant: 'error' });
      return;
    }

    setAppliedButNotSavedRooms([...nonMasterSupplierMappedRooms, ...appliedButNotSavedRooms]);

    // update payload
    const newMappings = nonMasterSupplierMappedRooms.map((sr) => formatSourceRoom(sr, sr.supplier, room.id, true));
    const newUpload = new Set([...updatePayload, ...newMappings]); // using set to ensure duplicates are trimmed
    setUpdatePayload(Array.from(newUpload));
    handleChange({
      update: Array.from(newUpload),
      remove: removePayload,
      automappingFlagUpdate: automappingFlagPayload,
    });
    resetPropertySearch();
    // clear source rooms from selection
    setSelectedSourceRooms([]);
  };

  /**
   * Handles quick mapping of a source room (or all rooms in a group) to a property room via ML suggestions
   * @param sourceRoom The source room to map (can be a single room or reference room from a group)
   * @param targetPropertyRoomName The target property room name
   * @param roomGroup Optional - the full group if mapping all rooms in a group
   */
  const handleQuickMap = useCallback(
    (sourceRoom: SourceRoom, targetPropertyRoomName: string, roomGroup?: SourceRoomListItem) => {
      // Prevent double-clicks and rapid successive calls
      if (quickMapInProgressRef.current) {
        return;
      }

      quickMapInProgressRef.current = true;
      setIsQuickMapping(true);

      try {
        // Determine if we're mapping a group or a single room
        const isGroupMapping = roomGroup && isSourceRoomGroup(roomGroup);
        const roomsToMap = isGroupMapping ? roomGroup.rooms : [sourceRoom];

        // Find the property room by supplier name
        const targetRoom = propertyRooms.find((pr) => pr.name === targetPropertyRoomName);

        if (!targetRoom) {
          enqueueSnackbar('Target property room not found', { variant: 'error' });
          return;
        }

        // Validate all rooms before mapping
        const validRooms: Array<SourceRoom> = [];
        const skippedRooms: Array<string> = [];

        for (const room of roomsToMap) {
          // CHECK 1: Skip if already in pending update payload
          const existingMapping = updatePayload.find(
            (mapping) => mapping.supplierRoomId === room.externalId && mapping.supplier === room.supplier,
          );

          if (existingMapping) {
            skippedRooms.push(`${room.name} (pending)`);
            continue;
          }

          // CHECK 2: Skip if already mapped in the database
          const isAlreadyMapped = alreadyMappedRooms[`${room.supplier}-${room.externalId}`];
          if (isAlreadyMapped) {
            skippedRooms.push(`${room.name} (already mapped)`);
            continue;
          }

          validRooms.push(room);
        }

        // If no valid rooms to map, show warning and return
        if (validRooms.length === 0) {
          if (isGroupMapping) {
            enqueueSnackbar(`All rooms in the group are already mapped. Skipped: ${skippedRooms.join(', ')}`, {
              variant: 'warning',
            });
          } else {
            enqueueSnackbar(
              `Source room "${sourceRoom.name}" is already mapped. Please remove the existing mapping first.`,
              {
                variant: 'warning',
              },
            );
          }
          return;
        }

        // Create mappings for all valid rooms
        const newMappings = validRooms.map((room) => formatSourceRoom(room, room.supplier, targetRoom.id, true));

        // Add to applied but not saved rooms for UI feedback
        setAppliedButNotSavedRooms((current) => {
          const uniqueRooms = validRooms.filter(
            (room) => !current.some((sr) => sr.externalId === room.externalId && sr.supplier === room.supplier),
          );
          return [...current, ...uniqueRooms];
        });

        // Add to update payload with proper deduplication
        setUpdatePayload((current) => {
          // Filter out any existing mappings for these source rooms
          const filtered = current.filter(
            (m) => !validRooms.some((room) => m.supplierRoomId === room.externalId && m.supplier === room.supplier),
          );

          const updatedArray = [...filtered, ...newMappings];

          handleChange({
            update: updatedArray,
            remove: removePayload,
            automappingFlagUpdate: automappingFlagPayload,
          });

          return updatedArray;
        });

        // Clear any existing property room filter
        resetPropertySearch();

        // Select the property room to highlight it
        setSelectedPropertyRoom(`${targetRoom.supplier}-${targetRoom.externalId}`);

        // Show success message
        if (isGroupMapping) {
          const message =
            skippedRooms.length > 0
              ? `Mapped ${validRooms.length} room(s) to ${targetPropertyRoomName}. Skipped: ${skippedRooms.join(', ')}`
              : `Mapped all ${validRooms.length} room(s) in group to ${targetPropertyRoomName} (pending verification)`;
          enqueueSnackbar(message, { variant: 'success' });
        } else {
          enqueueSnackbar(`Mapped ${sourceRoom.name} to ${targetPropertyRoomName} (pending verification)`, {
            variant: 'info',
          });
        }

        // Clear any existing scroll timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        // Scroll target row into view. With virtualisation the row's DOM ref is
        // null when off-screen, so drive scroll through Virtuoso's scrollToIndex.
        scrollTimeoutRef.current = setTimeout(() => {
          requestAnimationFrame(() => {
            const targetIndex = filteredPropertyRooms.findIndex((room) => room.id === targetRoom.id);
            if (targetIndex >= 0) {
              propertyVirtuosoRef.current?.scrollToIndex({ index: targetIndex, align: 'center', behavior: 'smooth' });
            }
          });
        }, SCROLL_DELAY_MS);
      } finally {
        // Reset debounce flag after a short delay
        setTimeout(() => {
          quickMapInProgressRef.current = false;
          setIsQuickMapping(false);
        }, QUICK_MAP_DEBOUNCE_MS);
      }
    },
    [
      propertyRooms,
      removePayload,
      automappingFlagPayload,
      handleChange,
      enqueueSnackbar,
      updatePayload,
      alreadyMappedRooms,
      resetPropertySearch,
      formatSourceRoom,
      filteredPropertyRooms,
    ],
  );

  /**
   * Handles removal of mapped rooms and updates automapping flags
   */
  const handleRemove = (mappedRooms: MappedRoom | Array<MappedRoom>) => {
    const roomsToRemove = Array.isArray(mappedRooms) ? mappedRooms : [mappedRooms];

    const remove: App.Bedbank.SupplierRoomMappingPayload = [...removePayload];
    const newAlreadyMappedRooms = { ...alreadyMappedRooms };

    // Build all automapping flag changes at once
    const automappingChanges: Array<AutomappingFlagToggleTarget> = [];

    for (const mappedRoom of roomsToRemove) {
      remove.push(formatSourceRoom(mappedRoom, mappedRoom.supplier, mappedRoom.roomId));

      // Always disable automapping when unmapping a room
      automappingChanges.push({
        supplierRoomId: mappedRoom.externalId,
        externalPropertyId: mappedRoom.externalPropertyId,
        supplier: mappedRoom.supplier,
        isAutoMappingEnabled: false,
      });

      delete newAlreadyMappedRooms[`${mappedRoom.supplier}-${mappedRoom.externalId}`];
    }

    // Apply all changes at once
    const newAutomappingFlagPayload = applyAutomappingFlagChanges(automappingFlagPayload, automappingChanges);

    // Update local state to reflect the disabled automapping immediately in UI
    setPropertyRooms((rooms) =>
      rooms.map((room) => {
        const updatedMappedRooms = room.mappedRooms.map((mappedRoom) => {
          const shouldDisableAutomapping = roomsToRemove.some(
            (removedRoom) =>
              removedRoom.supplier === mappedRoom.supplier && removedRoom.externalId === mappedRoom.externalId,
          );

          if (shouldDisableAutomapping && mappedRoom.sourceContent) {
            return {
              ...mappedRoom,
              sourceContent: {
                ...mappedRoom.sourceContent,
                isAutoMappingEnabled: false,
              },
            };
          }

          return mappedRoom;
        });

        if (updatedMappedRooms !== room.mappedRooms) {
          return {
            ...room,
            mappedRooms: updatedMappedRooms,
          };
        }

        return room;
      }),
    );

    setSourceRooms((currentSourceRooms) => {
      const nextSourceRooms = { ...currentSourceRooms };

      roomsToRemove.forEach(({ supplier, externalId }) => {
        const list = nextSourceRooms[supplier];
        if (!list) {
          return;
        }

        nextSourceRooms[supplier] = list.map((sourceRoom) =>
          sourceRoom.externalId === externalId
            ? {
                ...sourceRoom,
                isAutoMappingEnabled: false,
              }
            : sourceRoom,
        );
      });

      return nextSourceRooms;
    });

    setAlreadyMappedRooms(newAlreadyMappedRooms);
    setRemovePayload(remove);
    setAutomappingFlagPayload(newAutomappingFlagPayload);

    handleChange({
      update: updatePayload,
      remove,
      automappingFlagUpdate: newAutomappingFlagPayload,
    });
  };

  const verifyMappedRoomModel = (mappedRoomModel: MappedRoom) => {
    setUpdatePayload((currentPayload) => {
      const newUpdate: App.Bedbank.SupplierRoomMappingPayload[number] = {
        roomId: mappedRoomModel.roomId,
        supplierRoomId: mappedRoomModel.externalId,
        supplierPropertyId: mappedRoomModel.externalPropertyId,
        supplier: mappedRoomModel.supplier,
        mappingMethod: ['manual'],
        isVerified: true,
      };
      return [...currentPayload, newUpdate];
    });

    setAppliedButNotSavedRooms((current) => [...current, mappedRoomModel.sourceContent]);
  };

  const fetchData = useCallback(async () => {
    const alreadyMapped: Record<string, boolean> = {};
    const fetchedSourceRooms = await getSourceRooms();

    // Enable ML matching loading now that source rooms are available
    setSourceRoomsLoaded(true);

    setPropertyRooms(
      property.rooms.map((room: Room) => {
        const {
          id,
          name,
          descriptionOverride,
          mappedRooms: mappedRoomsModel,
          supplier,
          externalId,
          status,
          nameOverride,
          nameOverrideSource,
          useOverrideNameForMapping,
        } = room;
        const sourceRoom = mappedRoomsModel.find((sr) => sr.externalId === externalId && sr.supplier === supplier);
        return {
          id,
          name,
          nameOverride,
          nameOverrideSource,
          useOverrideNameForMapping: useOverrideNameForMapping ?? false,
          description: descriptionOverride,
          externalId: sourceRoom?.externalId ?? externalId,
          mappedRooms: mappedRoomsModel
            .map<MappedRoom>((mappedRoom: App.Bedbank.MappedRoom) => {
              alreadyMapped[`${mappedRoom.supplier}-${mappedRoom.externalId}`] = true;
              const supportedMappingMethods = (mappedRoom.mappingMethod || []).filter(
                (method): method is 'manual' | 'sync' | 'string' | 'vervotech' | 'gimmonix' | 'import' | 'ml' =>
                  ['manual', 'sync', 'string', 'vervotech', 'gimmonix', 'import', 'ml'].includes(method),
              );
              return {
                ...mappedRoom,
                mappingMethod: supportedMappingMethods.length > 0 ? supportedMappingMethods : undefined,
                sourceContent: fetchedSourceRooms[mappedRoom.supplier]?.find(
                  (sp) => sp.externalId === mappedRoom.externalId,
                ),
              };
            })
            .filter((mappedRoom) => {
              if (mappedRoom.sourceContent === undefined) {
                console.error(
                  `Source content not found for mapped room with room ID ${mappedRoom.roomId}, external ID ${mappedRoom.externalId}, supplier ${mappedRoom.supplier}.`,
                );
                enqueueSnackbar(
                  `Source content not found for mapped room with room ID ${mappedRoom.roomId}, external ID ${mappedRoom.externalId}, supplier ${mappedRoom.supplier}. Please flag this to a member of hotels team.`,
                  { variant: 'error' },
                );
                return false;
              }
              return true;
            })
            .sort((a, b) => sortSourceRoom(a.sourceContent, b.sourceContent)),
          supplier,
          status,
        };
      }),
    );
    setSourceRooms(fetchedSourceRooms);
    setAlreadyMappedRooms(alreadyMapped);
    setIsLoading(false);
  }, [getSourceRooms, property.rooms, enqueueSnackbar]);

  const clearNewMappings = useCallback(
    (room: PropertyRoom) => {
      setUpdatePayload((currentPayload) => {
        const newMappings = currentPayload.filter((update) => update.roomId !== room.id);

        // Any applied but not saved rooms that is not in the update payload should be cleared (only ones in the update payload should be kept)
        setAppliedButNotSavedRooms((currentApplied) =>
          currentApplied.filter((r) => newMappings.some((mappedRoom) => mappedRoom.supplierRoomId === r.externalId)),
        );

        handleChange({
          update: newMappings,
          remove: removePayload,
          automappingFlagUpdate: automappingFlagPayload,
        });

        return newMappings;
      });
    },
    [handleChange, removePayload, automappingFlagPayload],
  );

  /**
   * Removes a single pending mapping (not yet saved) and returns it to source rooms
   */
  const removeSingleNewMapping = useCallback(
    (roomId: string, supplierRoomId: string, supplier: string) => {
      setUpdatePayload((currentPayload) => {
        const newMappings = currentPayload.filter(
          (update) =>
            !(update.roomId === roomId && update.supplierRoomId === supplierRoomId && update.supplier === supplier),
        );

        // Remove the specific source room from applied but not saved list
        setAppliedButNotSavedRooms((currentApplied) =>
          currentApplied.filter((r) => !(r.externalId === supplierRoomId && r.supplier === supplier)),
        );

        handleChange({
          update: newMappings,
          remove: removePayload,
          automappingFlagUpdate: automappingFlagPayload,
        });

        return newMappings;
      });
    },
    [handleChange, removePayload, automappingFlagPayload],
  );

  // Create a memoized map of clear functions for each room to prevent re-renders
  const clearNewMappingsByRoomId = useMemo(() => {
    const map = new Map<string, () => void>();
    filteredPropertyRooms.forEach((room) => {
      map.set(room.id, () => clearNewMappings(room));
    });
    map.set('__noop__', NOOP);
    return map;
  }, [filteredPropertyRooms, clearNewMappings]);

  // Create a memoized map of remove single mapping functions for each room to prevent re-renders
  const removeSingleMappingByRoomId = useMemo(() => {
    const map = new Map<string, (supplierRoomId: string, supplier: string) => void>();
    filteredPropertyRooms.forEach((room) => {
      map.set(room.id, (supplierRoomId: string, supplier: string) =>
        removeSingleNewMapping(room.id, supplierRoomId, supplier),
      );
    });
    map.set('__noop__', () => NOOP);
    return map;
  }, [filteredPropertyRooms, removeSingleNewMapping]);

  useEffect(() => {
    async function firstLoad() {
      setIsLoading(true);
      await fetchData();
    }
    firstLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Fetch batch ML matching candidates when source rooms are loaded

  useEffect(() => {
    handleChange({
      update: updatePayload,
      remove: removePayload,
      automappingFlagUpdate: automappingFlagPayload,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePayload, removePayload, automappingFlagPayload]); // handleChange is provided by parent and should not trigger re-runs

  const renderSourceRoomItem = (_ind: number, room: SourceRoomListItem) => {
    const isSelected = isSourceRoomGroup(room)
      ? room.rooms.every((childRoom) =>
          selectedSourceRooms.includes(`${childRoom.supplier}-${childRoom.externalId}`),
        )
      : selectedSourceRooms.includes(`${room.supplier}-${room.externalId}`);

    const supplierForRoom = room.supplier;
    const isRoomMapped = isSourceRoomGroup(room)
      ? room.rooms.some((childRoom) => alreadyMappedRooms[`${childRoom.supplier}-${childRoom.externalId}`])
      : alreadyMappedRooms[`${room.supplier}-${room.externalId}`];

    const shouldShowMLMatching = !isRoomMapped || enableMLMatching;
    const referenceRoom = isSourceRoomGroup(room) ? room.rooms[0] : room;
    const sourceRoomName = referenceRoom.name;
    const sourceRoomResult =
      shouldShowMLMatching && sourceRoomName ? mlMatchingCandidates[sourceRoomName] : undefined;
    const mlMatchingData = sourceRoomResult
      ? {
          ranking: sourceRoomResult.candidates.map((c) => ({
            roomName: c.masterRoomName,
            score: c.score,
            jaccard: c.jaccard,
          })),
          confidenceThreshold: sourceRoomResult.confidenceThreshold,
        }
      : undefined;

    return (
      <Box sx={{ pb: 2 }}>
        <SourceRoomElement
          room={room}
          onSelect={handleSourceRoomSelection}
          isSelected={isSelected}
          automappingFlagPayload={automappingFlagPayload}
          handleSetAutomappingFlags={handleSetAutomappingFlags}
          supplier={supplierForRoom}
          candidateRooms={shouldShowMLMatching ? filteredPropertyRooms : undefined}
          onUngroupRoom={handleUngroupRoom}
          onQuickMap={handleQuickMap}
          appliedButNotSavedRooms={appliedButNotSavedRooms}
          isQuickMapping={isQuickMapping}
          onUpdateMLMatchScore={updateMLMatchScore}
          searchTerm={deferredSourceSearchTerm}
          mlMatchingData={mlMatchingData}
          isLoadingMLCandidates={isLoadingMLCandidates}
        />
      </Box>
    );
  };

  const renderPropertyRoomItem = (_ind: number, room: PropertyRoom) => (
    <Box sx={{ pb: 2 }}>
      <ControlRoomElement
        ref={(el) => {
          propertyRoomRefs.current[room.id] = el;
        }}
        room={room}
        property={property}
        isSelected={selectedPropertyRoom === `${room.supplier}-${room.externalId}`}
        onSelect={handlePropertyRoomSelection}
        onRemoveMappedRoom={handleRemove}
        verifyMappedRoomModel={verifyMappedRoomModel}
        newMappings={updates[room.id] ?? []}
        appliedButNotSavedRooms={appliedButNotSavedRooms}
        clearNewMappings={
          (clearNewMappingsByRoomId.get(room.id) ?? clearNewMappingsByRoomId.get('__noop__')) || NOOP
        }
        removeSingleMapping={
          (removeSingleMappingByRoomId.get(room.id) ?? removeSingleMappingByRoomId.get('__noop__')) ||
          (() => NOOP)
        }
        removePayload={removePayload}
        automappingFlagPayload={automappingFlagPayload}
        handleSetAutomappingFlags={handleSetAutomappingFlags}
        enableMLMatching={enableMLMatching}
        searchTerm={deferredPropertySearchTerm}
        matchedMappingIds={verifiedMappingMatchesByRoomId[room.id]}
        mlMatchingCandidates={mlMatchingCandidates}
        mappedSupplierFilter={propertySupplierFilter || undefined}
        extraInclusionsByKey={extraInclusionsByKey}
      />
    </Box>
  );

  return (
    <Container maxWidth="xl" disableGutters>
      {(updatePayload.length > 0 || removePayload.length > 0 || automappingFlagPayload.length > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Stack direction="column" spacing={0.5}>
            <Typography variant="body2" fontWeight="bold">
              ⚠️ You have unsaved changes - Remember to save!
            </Typography>
            {updatePayload.length > 0 && (
              <Typography variant="caption">• {updatePayload.length} room mapping(s) to apply</Typography>
            )}
            {removePayload.length > 0 && (
              <Typography variant="caption">• {removePayload.length} room mapping(s) to remove</Typography>
            )}
            {automappingFlagPayload.length > 0 && (
              <Typography variant="caption">
                • {automappingFlagPayload.length} automapping toggle(s) to update
              </Typography>
            )}
          </Stack>
        </Alert>
      )}
      <Stack direction="row" spacing={2} mb={2} justifyContent="space-between">
        <Typography variant="h6" display="flex" justifyContent="space-evenly" width="100%">
          {updatePayload.length === 0 &&
            removePayload.length === 0 &&
            automappingFlagPayload.length === 0 &&
            'Map Source Rooms to Property Rooms'}
        </Typography>
      </Stack>
      {isLoading && (
        <Box m={2}>
          <LinearProgress />
        </Box>
      )}
      <Box display="grid" gridTemplateColumns="480px 1fr" gap={3}>
        {/* Container for Suppliers */}
        <Box position="relative">
          <Stack direction="column" gap={2} position="sticky" top={0} overflow="auto" height="calc(100dvh - 56px)">
            <Paper square sx={{ position: 'sticky', top: 0, zIndex: 1, pt: 1 }}>
              <Stack direction="column" gap={2}>
                <Typography variant="h6">Source Rooms: {selectedFromSourceRooms}</Typography>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ md: 'flex-end' }}
                  flexWrap="wrap"
                >
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Search source rooms"
                    value={sourceSearchTerm}
                    placeholder={isFilteringSourceRooms ? 'Filtering...' : 'Search by name, code, or inclusion'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {isFilteringSourceRooms && <Typography variant="caption">Filtering...</Typography>}
                          <IconButton
                            onClick={resetSourceSearch}
                            disabled={sourceSearchTerm.length < 1}
                            title="Clear search"
                            edge="end"
                            size="small"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    onChange={handleSourceSearchChange}
                  />
                  <Stack direction="column" spacing={0.5} sx={{ minWidth: 160, maxWidth: 200 }}>
                    <Typography variant="caption" color="text.secondary">
                      Supplier
                    </Typography>
                    <Select size="small" value={sourceSupplierFilter} onChange={handleSourceSupplierFilterChange}>
                      <MenuItem value="">All suppliers</MenuItem>
                      {SUPPLIER_NAMES.filter((supplier) => (sourceRooms[supplier] ?? []).length > 0).map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                </Stack>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ md: 'center' }}
                  flexWrap="wrap"
                >
                  <Stack direction="column" spacing={0.5} sx={{ minWidth: 160, maxWidth: 200 }}>
                    <Typography variant="caption" color="text.secondary">
                      Sort by
                    </Typography>
                    <Select size="small" value={sortSourceRoomsBy} onChange={handleSortChange}>
                      <MenuItem value="ml-score">ML Match Score</MenuItem>
                      <MenuItem value="availability">Availability Score</MenuItem>
                    </Select>
                  </Stack>
                  <Tooltip
                    title="Availability score is a measure of how often a room has available rates"
                    placement="right-end"
                  >
                    <FormControlLabel
                      checked={hideRoomsWithNoAvailabilityScore}
                      onChange={toggleHideRoomsWithNoAvailabilityScore}
                      control={<Checkbox />}
                      label="Hide rooms with no availability score"
                    />
                  </Tooltip>
                  <FormControlLabel
                    checked={showOnlyRoomsWithInclusions}
                    onChange={toggleShowOnlyRoomsWithInclusions}
                    control={<Checkbox />}
                    label="Has inclusions"
                  />
                  <Button
                    variant="contained"
                    startIcon={<AccountTreeIcon />}
                    title="Force decouple"
                    onClick={() =>
                      onForceDecoupleRoomsClick(
                        selectedSourceRooms
                          .map((key) => {
                            const [supplier, externalId] = extractKey(key);
                            return sourceRooms[supplier].find((sr: SourceRoom) => sr.externalId === externalId)?.id;
                          })
                          .filter(Boolean),
                      )
                    }
                    disabled={!selectedSourceRooms.length}
                  >
                    Force decouple
                  </Button>
                </Stack>
                <Divider />
              </Stack>
            </Paper>
            {exceedsMLAutoFetchThreshold && !manualMLFetchRequested && (
              <Alert
                severity="info"
                action={
                  <Button color="inherit" size="small" onClick={handleManualMLFetch} disabled={isLoadingMLCandidates}>
                    Load ML Data
                  </Button>
                }
              >
                <Typography variant="body2">
                  <strong>ML matching disabled due to large dataset</strong>
                </Typography>
                <Typography variant="caption">
                  This property has {totalSourceRoomCount} source rooms and {totalPropertyRoomCount} property rooms.
                  Loading ML matching data for large datasets may take longer. Click the button to load ML suggestions.
                </Typography>
              </Alert>
            )}
            {exceedsMLAutoFetchThreshold && manualMLFetchRequested && isLoadingMLCandidates && (
              <Alert severity="info">
                <Typography variant="body2">
                  Loading ML matching data for {totalSourceRoomCount} source rooms...
                </Typography>
              </Alert>
            )}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {(sourceSearchTerm || sourceSupplierFilter || showOnlyRoomsWithInclusions) &&
              filteredUnmappedSourceRooms.length === 0 ? (
                <Typography>No rooms found based on filter</Typography>
              ) : groupedUnmappedSourceRooms.length === 0 ? (
                <Typography color="text.secondary">No unmapped source rooms</Typography>
              ) : (
                <Virtuoso
                  data={groupedUnmappedSourceRooms}
                  style={{ height: VIRTUOSO_LIST_HEIGHT }}
                  computeItemKey={(ind, room) =>
                    isSourceRoomGroup(room)
                      ? `${room.supplier}-${room.supplierRoomName}-group-${ind}`
                      : `${room.supplier}-${room.externalId}-${ind}`
                  }
                  itemContent={renderSourceRoomItem}
                />
              )}
            </Box>
          </Stack>
        </Box>
        {/* Container for LE/MasterSupplier Rooms */}
        <Stack direction="column" gap={2}>
          <Paper square sx={{ position: 'sticky', top: 0, zIndex: 1, pt: 1 }}>
            <Stack direction="column" gap={2}>
              <Typography variant="h6">Property Rooms: {selectedFromPropertyRooms}</Typography>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ md: 'flex-end' }}
                flexWrap="wrap"
              >
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Search property rooms"
                  value={propertySearchTerm}
                  placeholder={isFilteringPropertyRooms ? 'Filtering...' : 'Search by name or code'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {isFilteringPropertyRooms && <Typography variant="caption">Filtering...</Typography>}
                        <IconButton
                          onClick={resetPropertySearch}
                          disabled={propertySearchTerm.length < 1}
                          title="Clear search"
                          edge="end"
                          size="small"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onChange={handlePropertySearchChange}
                />
                <Stack direction="column" spacing={0.5} sx={{ minWidth: 160, maxWidth: 200 }}>
                  <Typography variant="caption" color="text.secondary">
                    Supplier
                  </Typography>
                  <Select size="small" value={propertySupplierFilter} onChange={handlePropertySupplierFilterChange}>
                    <MenuItem value="">All suppliers</MenuItem>
                    {propertyRoomSuppliers.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              </Stack>
              <FormControlLabel
                checked={enableMLMatching}
                onChange={toggleMLMatching}
                control={<Checkbox />}
                label="Enable ML matching for already mapped source rooms"
              />
              <Button
                variant="contained"
                startIcon={<AccountTreeIcon />}
                title="Map selected source rooms to the selected Room"
                onClick={handleUpdateMapping}
                disabled={selectedSourceRooms.length === 0 || !selectedPropertyRoom}
              >
                Apply selected Mapping
              </Button>
              <Divider />
            </Stack>
          </Paper>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {propertySearchTerm && filteredPropertyRooms.length === 0 ? (
              <Typography>No rooms found based on filter</Typography>
            ) : filteredPropertyRooms.length === 0 ? (
              <Typography color="text.secondary">No property rooms</Typography>
            ) : (
              <Virtuoso
                ref={propertyVirtuosoRef}
                data={filteredPropertyRooms}
                style={{ height: VIRTUOSO_LIST_HEIGHT }}
                computeItemKey={(_ind, room) => room.id}
                itemContent={renderPropertyRoomItem}
              />
            )}
          </Box>
        </Stack>
      </Box>
    </Container>
  );
}

export default BedbankRoomMapping;
